/**
 * Tawk.to live chat integration.
 *
 * SDK loading: NOT loaded by tazio-fe. Tawk widget script is injected
 *              externally (historically by the customer's portal SSO layer).
 *              No <script> tag in public/index.html, no NPM dependency.
 * Global:      window.Tawk_API
 * Env vars:    none
 *
 * Status: legacy — Zendesk is the active live chat. Tawk hooks remain
 *         for backwards compatibility with portals that still embed Tawk.
 *
 * Existing call sites (as of 2026-04-13):
 *   - src/config.js  tawk_set_sso_attributes() inside trackSsoEvents()
 *
 * Naming convention: every public function is prefixed with `tawk_` so the
 * Tawk origin is obvious at every call site, even when imported alongside
 * other integration modules.
 */

export function tawk_is_available() {
  return Boolean(window.Tawk_API)
}

/**
 * Set user attributes on the Tawk widget. Safe to call before the widget
 * has finished loading: falls back to onLoad in that case.
 *
 * @param {Object} attributes - Arbitrary key/value pairs (name, email, token, ...).
 * @param {Function} [on_error] - Optional callback receiving the SDK error.
 */
export function tawk_set_attributes(attributes, on_error) {
  if (!tawk_is_available()) return

  const error_handler = function (error) {
    if (!error) return
    window.console.log('tawk setAttributes error')
    window.console.log(error)
    if (typeof on_error === 'function') on_error(error)
  }

  if (window.Tawk_API.setAttributes !== undefined) {
    window.Tawk_API.setAttributes(attributes, error_handler)
    return
  }

  // setAttributes not yet wired: defer until the widget signals onLoad.
  // NOTE: this overwrites any existing `Tawk_API.onLoad` handler. Acceptable
  // because tazio-fe is the only caller and this path is legacy (Zendesk is
  // the active chat). Revisit if Tawk is re-activated.
  window.Tawk_API.onLoad = function () {
    window.Tawk_API.setAttributes(attributes, function (error) {
      if (!error) return
      window.console.log('tawk onLoad setAttributes error')
      window.console.log(error)
      if (typeof on_error === 'function') on_error(error)
    })
  }
}

/**
 * Set SSO user attributes using the canonical mapping that tazio-fe
 * has historically pushed to Tawk (name, email, token, portale, azienda,
 * credito).
 *
 * @param {Object} sso - { displayName, email, token, customer, company, availability }
 */
export function tawk_set_sso_attributes(sso) {
  tawk_set_attributes({
    name: sso.displayName,
    email: sso.email,
    token: sso.token,
    portale: sso.customer,
    azienda: sso.company,
    credito: sso.availability,
  })
}

/**
 * Toggle the Tawk widget visibility (open if closed, close if open).
 * Was used historically as `javascript:window.Tawk_API.toggle()` link.
 */
export function tawk_toggle() {
  if (!tawk_is_available()) return
  if (typeof window.Tawk_API.toggle !== 'function') return
  window.Tawk_API.toggle()
}
